import { createRouter, createWebHistory } from "vue-router"
import { t } from "@/plugins/i18n/i18n"
import LoadingView from "@/features/LoadingView.vue"
import TransitionView from "@/features/TransitionView.vue"
import { usePreviousStateModalStore } from "@/components/af/PreviousStateModal/previousStateModal.store"
import { useLeaveAppModalStore } from "@/components/af/LeaveAppModal/leaveAppModal.store"
import { storeToRefs } from "pinia"
import {
  MixpanelInstance,
  isMixpanelInitialized,
} from "@/plugins/mixpanel/mixpanel"
import { useWindowScroll } from "@vueuse/core"
import ContactSupportView from "@/features/ContactSupportView.vue"
import MaintenanceView from "@/features/MaintenanceView.vue"

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    {
      path: "/",
      name: "start",
      meta: { title: "candidate_account.manage_process.meta.title" },
      component: LoadingView,
    },
    // use two root routes to handle the first navigation and push history state
    {
      path: "/loading",
      name: "loading",
      meta: { title: "candidate_account.manage_process.meta.title" },
      component: LoadingView,
    },
    {
      path: "/transition",
      name: "transition",
      meta: { title: "candidate_account.manage_process.meta.title" },
      component: TransitionView,
    },
    {
      path: "/maintenance",
      name: "maintenance",
      meta: { title: "candidate_account.manage_process.meta.title" },
      component: MaintenanceView,
    },
    {
      path: "/contact-support",
      name: "contactSupport",
      meta: { title: "candidate_account.manage_process.meta.title" },
      component: ContactSupportView,
    },
    {
      path: "/steps",
      name: "stepper",
      meta: { title: "candidate_account.manage_process.meta.title" },
      component: () => import("@/features/stepper/StepperView.vue"),
    },
    {
      path: "/swipe",
      name: "swipe",
      meta: { title: "swipe.meta.title" },
      component: () => import("@/features/swipe/Swipe.vue"),
      // redirect to swipe intro is handled in the swipeMachine
      children: [
        {
          path: "loading",
          name: "swipeLoading",
          meta: { title: "swipe.meta.title" },
          component: LoadingView,
        },
        {
          path: "intro",
          name: "swipeIntro",
          meta: { title: "swipe.meta.title" },
          component: () => import("@/features/swipe/views/SwipeIntro.vue"),
        },
        {
          path: "welcome-back",
          name: "swipeWelcomeBack",
          meta: { title: "swipe.meta.title" },
          component: () =>
            import("@/features/swipe/views/SwipeWelcomeBack.vue"),
        },
        {
          path: "tutorial",
          name: "swipeTuto",
          meta: { title: "swipe.meta.title" },
          component: () => import("@/features/swipe/views/SwipeTuto.vue"),
        },
        {
          path: "trial",
          name: "swipeTrial",
          meta: { title: "swipe.meta.title" },
          component: () => import("@/features/swipe/views/SwipeTrial.vue"),
        },
        {
          path: "test",
          name: "swipeTest",
          meta: { title: "swipe.meta.title" },
          component: () => import("@/features/swipe/views/SwipeTest.vue"),
        },
        {
          path: "native-language",
          name: "swipeNativeLanguage",
          meta: { title: "swipe.meta.title" },
          component: () =>
            import("@/features/swipe/views/SwipeNativeLanguage.vue"),
        },
        {
          path: "wholesome-conditions",
          name: "swipeWholesomeConditions",
          meta: { title: "swipe.meta.title" },
          component: () =>
            import("@/features/swipe/views/SwipeWholesomeConditions.vue"),
        },
        {
          path: "restart",
          name: "swipeRestart",
          meta: { title: "swipe.meta.title" },
          component: () => import("@/features/swipe/views/SwipeRestart.vue"),
        },
        {
          path: "load-results",
          name: "swipeLoadResults",
          meta: { title: "swipe.meta.title" },
          component: () =>
            import("@/features/swipe/views/SwipeLoadResults.vue"),
        },
      ],
    },
    {
      path: "/shape",
      name: "shape",
      meta: { title: "shape.meta.title" },
      component: () => import("@/features/shape/Shape.vue"),
      // redirect to shape intro is handled in the shapeMachine
      children: [
        {
          path: "loading",
          name: "shapeLoading",
          meta: { title: "shape.meta.title" },
          component: LoadingView,
        },
        {
          path: "intro",
          name: "shapeIntro",
          meta: { title: "shape.meta.title" },
          component: () => import("@/features/shape/views/ShapeIntro.vue"),
        },
        {
          path: "welcome-back",
          name: "shapeWelcomeBack",
          meta: { title: "shape.meta.title" },
          component: () =>
            import("@/features/shape/views/ShapeWelcomeBack.vue"),
        },
        {
          path: "rules",
          name: "shapeRules",
          meta: { title: "shape.meta.title" },
          component: () => import("@/features/shape/views/ShapeRules.vue"),
        },
        {
          path: "practice",
          name: "shapePractice",
          meta: { title: "shape.meta.title" },
          component: () => import("@/features/shape/views/ShapePractice.vue"),
        },
        {
          path: "tutorial",
          name: "shapeTuto",
          meta: { title: "shape.meta.title" },
          component: () => import("@/features/shape/views/ShapeTuto.vue"),
        },
        {
          path: "tutorial-confirmation",
          name: "shapeTutoEnd",
          meta: { title: "shape.meta.title" },
          component: () => import("@/features/shape/views/ShapeTutoEnd.vue"),
        },
        {
          path: "test",
          name: "shapeTest",
          meta: { title: "shape.meta.title" },
          component: () => import("@/features/shape/views/ShapeTest.vue"),
        },
        {
          path: "native-language",
          name: "shapeNativeLanguage",
          meta: { title: "shape.meta.title" },
          component: () =>
            import("@/features/shape/views/ShapeNativeLanguage.vue"),
        },
        {
          path: "wholesome-conditions",
          name: "shapeWholesomeConditions",
          meta: { title: "shape.meta.title" },
          component: () =>
            import("@/features/shape/views/ShapeWholesomeConditions.vue"),
        },
        {
          path: "restart",
          name: "shapeRestart",
          meta: { title: "shape.meta.title" },
          component: () => import("@/features/shape/views/ShapeRestart.vue"),
        },
        {
          path: "load-results",
          name: "shapeLoadResults",
          meta: { title: "shape.meta.title" },
          component: () =>
            import("@/features/shape/views/ShapeLoadResults.vue"),
        },
      ],
    },
    {
      path: "/drive",
      name: "drive",
      meta: { title: "drive.meta.title" },
      component: () => import("@/features/drive/Drive.vue"),
      // redirect to drive intro is handled in the driveMachine
      children: [
        {
          path: "loading",
          name: "driveLoading",
          meta: { title: "drive.meta.title" },
          component: LoadingView,
        },
        {
          path: "intro",
          name: "driveIntro",
          meta: { title: "drive.meta.title" },
          component: () => import("@/features/drive/views/DriveIntro.vue"),
        },
        {
          path: "welcome-back",
          name: "driveWelcomeBack",
          meta: { title: "drive.meta.title" },
          component: () =>
            import("@/features/drive/views/DriveWelcomeBack.vue"),
        },
        {
          path: "rules",
          name: "driveRules",
          meta: { title: "drive.meta.title" },
          component: () => import("@/features/drive/views/DriveRules.vue"),
        },
        {
          path: "practice",
          name: "drivePractice",
          meta: { title: "drive.meta.title" },
          component: () => import("@/features/drive/views/DrivePractice.vue"),
        },
        {
          path: "tutorial",
          name: "driveTuto",
          meta: { title: "drive.meta.title" },
          component: () => import("@/features/drive/views/DriveTuto.vue"),
        },
        {
          path: "tutorial-confirmation",
          name: "driveTutoEnd",
          meta: { title: "drive.meta.title" },
          component: () => import("@/features/drive/views/DriveTutoEnd.vue"),
        },
        {
          path: "test",
          name: "driveTest",
          meta: { title: "drive.meta.title" },
          component: () => import("@/features/drive/views/DriveTest.vue"),
        },
        {
          path: "native-language",
          name: "driveNativeLanguage",
          meta: { title: "drive.meta.title" },
          component: () =>
            import("@/features/drive/views/DriveNativeLanguage.vue"),
        },
        {
          path: "wholesome-conditions",
          name: "driveWholesomeConditions",
          meta: { title: "drive.meta.title" },
          component: () =>
            import("@/features/drive/views/DriveWholesomeConditions.vue"),
        },
        {
          path: "restart",
          name: "driveRestart",
          meta: { title: "drive.meta.title" },
          component: () => import("@/features/drive/views/DriveRestart.vue"),
        },
        {
          path: "load-results",
          name: "driveLoadResults",
          meta: { title: "drive.meta.title" },
          component: () =>
            import("@/features/drive/views/DriveLoadResults.vue"),
        },
      ],
    },
    {
      path: "/brain",
      name: "brain",
      meta: { title: "brain.meta.title" },
      component: () => import("@/features/brain/Brain.vue"),
      // redirect to brain intro is handled in the brainMachine
      children: [
        {
          path: "loading",
          name: "brainLoading",
          meta: { title: "brain.meta.title" },
          component: LoadingView,
        },
        {
          path: "intro",
          name: "brainIntro",
          meta: { title: "brain.meta.title" },
          component: () => import("@/features/brain/views/BrainIntro.vue"),
        },
        {
          path: "welcome-back",
          name: "brainWelcomeBack",
          meta: { title: "brain.meta.title" },
          component: () =>
            import("@/features/brain/views/BrainWelcomeBack.vue"),
        },
        {
          path: "rules",
          name: "brainRules",
          meta: { title: "brain.meta.title" },
          component: () => import("@/features/brain/views/BrainRules.vue"),
        },
        {
          path: "practice",
          name: "brainPractice",
          meta: { title: "brain.meta.title" },
          component: () => import("@/features/brain/views/BrainPractice.vue"),
        },
        {
          path: "tutorial",
          name: "brainTuto",
          meta: { title: "brain.meta.title" },
          component: () => import("@/features/brain/views/BrainTuto.vue"),
        },
        {
          path: "tutorial-confirmation",
          name: "brainTutoConfirmation",
          meta: { title: "brain.meta.title" },
          component: () => import("@/features/brain/views/BrainTutoEnd.vue"),
        },
        {
          path: "resume",
          name: "brainResume",
          meta: { title: "brain.meta.title" },
          component: () => import("@/features/brain/views/BrainResume.vue"),
        },
        {
          path: "breath",
          name: "brainBreath",
          meta: { title: "brain.meta.title" },
          component: () => import("@/features/brain/views/BrainBreath.vue"),
        },
        {
          path: "test",
          name: "brainTest",
          meta: { title: "brain.meta.title" },
          component: () => import("@/features/brain/views/BrainTest.vue"),
        },
        {
          path: "native-language",
          name: "brainNativeLanguage",
          meta: { title: "brain.meta.title" },
          component: () =>
            import("@/features/brain/views/BrainNativeLanguage.vue"),
        },
        {
          path: "wholesome-conditions",
          name: "brainWholesomeConditions",
          meta: { title: "brain.meta.title" },
          component: () =>
            import("@/features/brain/views/BrainWholesomeConditions.vue"),
        },
        {
          path: "restart",
          name: "brainRestart",
          meta: { title: "brain.meta.title" },
          component: () => import("@/features/brain/views/BrainRestart.vue"),
        },
        {
          path: "load-results",
          name: "brainLoadResults",
          meta: { title: "brain.meta.title" },
          component: () =>
            import("@/features/brain/views/BrainLoadResults.vue"),
        },
        {
          path: "confirmation",
          name: "brainConfirmation",
          meta: { title: "brain.meta.title" },
          component: () =>
            import("@/features/brain/views/BrainConfirmation.vue"),
        },
      ],
    },
    {
      path: "/voice",
      name: "voice",
      meta: { title: "voice.meta.title" },
      component: () => import("@/features/voice/Voice.vue"),
      // redirect to drive intro is handled in the driveMachine
      children: [
        {
          path: "loading",
          name: "voiceLoading",
          meta: { title: "voice.meta.title" },
          component: LoadingView,
        },
        {
          path: "intro",
          name: "voiceIntro",
          meta: { title: "voice.meta.title" },
          component: () => import("@/features/voice/views/VoiceIntro.vue"),
        },
        {
          path: "welcome-back",
          name: "voiceWelcomeBack",
          meta: { title: "voice.meta.title" },
          component: () =>
            import("@/features/voice/views/VoiceWelcomeBack.vue"),
        },
        {
          path: "rules",
          name: "voiceRules",
          meta: { title: "voice.meta.title" },
          component: () => import("@/features/voice/views/VoiceRules.vue"),
        },
        {
          path: "authorize",
          name: "voiceAuthorize",
          meta: { title: "voice.meta.title" },
          component: () => import("@/features/voice/views/VoiceAuthorize.vue"),
        },
        {
          path: "authorization-refused",
          name: "voiceAuthorizeRefused",
          meta: { title: "voice.meta.title" },
          component: () =>
            import("@/features/voice/views/VoiceAuthorizeRefused.vue"),
        },
        {
          path: "tutorial",
          name: "voiceTuto",
          meta: { title: "voice.meta.title" },
          component: () => import("@/features/voice/views/VoiceTuto.vue"),
        },
        {
          path: "test",
          name: "voiceTest",
          meta: { title: "voice.meta.title" },
          component: () => import("@/features/voice/views/VoiceTest.vue"),
        },
        {
          path: "feedback",
          name: "voiceFeedback",
          meta: { title: "voice.meta.title" },
          component: () => import("@/features/voice/views/VoiceFeedback.vue"),
        },
        {
          path: "end",
          name: "voiceEnd",
          meta: { title: "voice.meta.title" },
          component: () => import("@/features/voice/views/VoiceEnd.vue"),
        },
        {
          path: "load-results",
          name: "voiceLoadResults",
          meta: { title: "voice.meta.title" },
          component: () =>
            import("@/features/voice/views/VoiceLoadResults.vue"),
        },
      ],
    },
    {
      path: "/screening",
      name: "screening",
      meta: { title: "scan.meta.title" },
      component: () => import("@/features/screening/Screening.vue"),
      // redirect to drive intro is handled in the driveMachine
      children: [
        {
          path: "intro",
          name: "screeningIntro",
          meta: { title: "scan.meta.title" },
          component: () =>
            import("@/features/screening/views/ScreeningIntro.vue"),
        },
        {
          path: "authorize",
          name: "screeningAuthorize",
          meta: { title: "scan.meta.title" },
          component: () =>
            import("@/features/screening/views/ScreeningAuthorize.vue"),
        },
        {
          path: "loading",
          name: "screeningRoomLoading",
          meta: { title: "scan.meta.title" },
          component: () =>
            import("@/features/screening/views/room/ScreeningRoomLoader.vue"),
        },
        {
          path: "error",
          name: "screeningError",
          meta: { title: "scan.meta.title" },
          component: () =>
            import("@/features/screening/views/ScreeningError.vue"),
        },
        {
          path: "test",
          name: "screeningTest",
          meta: { title: "scan.meta.title" },
          component: () =>
            import("@/features/screening/views/room/ScreeningRoomAgent.vue"),
        },
        {
          path: "review",
          name: "screeningReview",
          meta: { title: "scan.meta.title" },
          component: () =>
            import("@/features/screening/views/ScreeningReview.vue"),
        },
        {
          path: "load-results",
          name: "screeningLoadResults",
          meta: { title: "scan.meta.title" },
          component: () =>
            import("@/features/screening/views/ScreeningLoadResults.vue"),
        },
      ],
    },
    {
      path: "/screening-cv",
      name: "screeningCv",
      meta: { title: "register_guest.meta.title" },
      component: () => import("@/features/screening-cv/ScreeningCv.vue"),
      children: [
        {
          path: "intro",
          name: "screeningCvIntro",
          meta: { title: "register_guest.meta.title" },
          component: () =>
            import("@/features/screening-cv/views/ScreeningCvIntro.vue"),
        },
        {
          path: "form",
          name: "screeningCvForm",
          meta: { title: "register_guest.meta.title" },
          component: () =>
            import("@/features/screening-cv/views/ScreeningCvUploadForm.vue"),
        },
        {
          path: "load-results",
          name: "screeningCvLoadResults",
          meta: { title: "register_guest.meta.title" },
          component: () =>
            import("@/features/screening-cv/views/ScreeningCvLoadResults.vue"),
        },
        {
          path: "error",
          name: "screeningCvError",
          meta: { title: "register_guest.meta.title" },
          component: () =>
            import("@/features/screening-cv/views/ScreeningCvError.vue"),
        },
      ],
    },
    {
      path: "/meeting",
      name: "meeting",
      meta: { title: "scan.meta.book_meeting" },
      component: () => import("@/features/book-meeting/BookMeeting.vue"),
    },
    {
      path: "/move",
      name: "move",
      meta: { title: "move.meta.title" },
      component: () => import("@/features/move/Move.vue"),
      // redirect to move intro is handled in the moveMachine
      children: [
        {
          path: "loading",
          name: "moveLoading",
          meta: { title: "move.meta.title" },
          component: LoadingView,
        },
        {
          path: "intro",
          name: "moveIntro",
          meta: { title: "move.meta.title" },
          component: () => import("@/features/move/views/MoveIntro.vue"),
        },
        {
          path: "welcome-back",
          name: "moveWelcomeBack",
          meta: { title: "move.meta.title" },
          component: () => import("@/features/move/views/MoveWelcomeBack.vue"),
        },
        {
          path: "rules",
          name: "moveRules",
          meta: { title: "move.meta.title" },
          component: () => import("@/features/move/views/MoveRules.vue"),
        },
        {
          path: "practice",
          name: "movePractice",
          meta: { title: "move.meta.title" },
          component: () => import("@/features/move/views/MovePractice.vue"),
        },
        {
          path: "tutorial",
          name: "moveTuto",
          meta: { title: "move.meta.title" },
          component: () => import("@/features/move/views/MoveTuto.vue"),
        },
        {
          path: "tutorial-confirmation",
          name: "moveTutoEnd",
          meta: { title: "move.meta.title" },
          component: () => import("@/features/move/views/MoveTutoEnd.vue"),
        },
        {
          path: "test",
          name: "moveTest",
          meta: { title: "move.meta.title" },
          component: () => import("@/features/move/views/MoveTest.vue"),
        },
        {
          path: "native-language",
          name: "moveNativeLanguage",
          meta: { title: "move.meta.title" },
          component: () =>
            import("@/features/move/views/MoveNativeLanguage.vue"),
        },
        {
          path: "wholesome-conditions",
          name: "moveWholesomeConditions",
          meta: { title: "move.meta.title" },
          component: () =>
            import("@/features/move/views/MoveWholesomeConditions.vue"),
        },
        {
          path: "load-results",
          name: "moveLoadResults",
          meta: { title: "move.meta.title" },
          component: () => import("@/features/move/views/MoveLoadResults.vue"),
        },
      ],
    },
    {
      path: "/movebis",
      name: "movebis",
      meta: { title: "movebis.meta.title" },
      component: () => import("@/features/movebis/MoveBis.vue"),
      // redirect to move intro is handled in the moveMachine
      children: [
        {
          path: "loading",
          name: "moveBisLoading",
          meta: { title: "movebis.meta.title" },
          component: LoadingView,
        },
        {
          path: "intro",
          name: "moveBisIntro",
          meta: { title: "movebis.meta.title" },
          component: () => import("@/features/movebis/views/MoveBisIntro.vue"),
        },
        {
          path: "welcome-back",
          name: "moveBisWelcomeBack",
          meta: { title: "movebis.meta.title" },
          component: () =>
            import("@/features/movebis/views/MoveBisWelcomeBack.vue"),
        },
        {
          path: "rules",
          name: "moveBisRules",
          meta: { title: "movebis.meta.title" },
          component: () => import("@/features/movebis/views/MoveBisRules.vue"),
        },
        {
          path: "practice",
          name: "moveBisPractice",
          meta: { title: "movebis.meta.title" },
          component: () =>
            import("@/features/movebis/views/MoveBisPractice.vue"),
        },
        {
          path: "tutorial",
          name: "moveBisTuto",
          meta: { title: "movebis.meta.title" },
          component: () => import("@/features/movebis/views/MoveBisTuto.vue"),
        },
        {
          path: "tutorial-confirmation",
          name: "moveBisTutoEnd",
          meta: { title: "movebis.meta.title" },
          component: () =>
            import("@/features/movebis/views/MoveBisTutoEnd.vue"),
        },
        {
          path: "test",
          name: "moveBisTest",
          meta: { title: "movebis.meta.title" },
          component: () => import("@/features/movebis/views/MoveBisTest.vue"),
        },
        {
          path: "native-language",
          name: "moveBisNativeLanguage",
          meta: { title: "movebis.meta.title" },
          component: () =>
            import("@/features/movebis/views/MoveBisNativeLanguage.vue"),
        },
        {
          path: "wholesome-conditions",
          name: "moveBisWholesomeConditions",
          meta: { title: "movebis.meta.title" },
          component: () =>
            import("@/features/movebis/views/MoveBisWholesomeConditions.vue"),
        },
        {
          path: "load-results",
          name: "moveBisLoadResults",
          meta: { title: "movebis.meta.title" },
          component: () =>
            import("@/features/movebis/views/MoveBisLoadResults.vue"),
        },
      ],
    },
    {
      path: "/thank-you",
      name: "thanksATS",
      meta: { title: "candidate_account.manage_process.meta.title" },
      component: () => import("@/features/thanksATSView.vue"),
    },
  ],
})

// this guard is to :
// - if firstNavigation : handle direct access to a page, before machine is started
// - if not firstNavigation and handle back button to display the leave modal
// (there is only one pushState in the app, so we can assume that the condition :
// to.name === "start" && (isTestDirectory || isMainDirectory) corresponds to a back button event)

let isFirstNavigation = true
router.beforeEach((to, from, next) => {
  const { y } = useWindowScroll({ behavior: "smooth" })
  if (y.value > 0) y.value = 0

  if (isFirstNavigation) {
    isFirstNavigation = false
    return next("/")
  }

  const testDirectories = [
    "swipe",
    "shape",
    "drive",
    "brain",
    "voice",
    "move",
    "movebis",
    "screening",
  ]
  const mainDirectories = ["stepper", "loading"]

  const isTestDirectory =
    from.matched.length > 0 &&
    testDirectories.includes(from.matched[0].name?.toString() || "")
  const isMainDirectory =
    from.matched.length > 0 &&
    mainDirectories.includes(from.matched[0].name?.toString() || "")

  // if we are in a test directory, we need to display the previous state modal
  // and repush the historyState removed by the back button event

  if (to.name === "start" && isTestDirectory) {
    const { isActive } = storeToRefs(usePreviousStateModalStore())
    history.pushState(null, "", from.fullPath)
    isActive.value = true // Display the previous state modal

    isMixpanelInitialized() &&
      MixpanelInstance.track("modal_viewed", {
        name: "previous_state_modal",
      })

    // if we are in the main directory, we need to display the leave app modal
    // and repush the historyState removed by the back button event
  } else if (to.name === "start" && isMainDirectory) {
    const { isActive } = storeToRefs(useLeaveAppModalStore())
    history.pushState(null, "", from.fullPath)
    isActive.value = true // Display the leave app modal
  } else {
    return next()
  }
})

// name of page visited before loading page
type RouteRecordName = string | symbol | undefined | null
let penultimatePage: RouteRecordName = ""
const pagesToIgnore: RouteRecordName[] = [
  "loading",
  "shapeLoading",
  "driveLoading",
  "brainLoading",
  "moveLoading",
  "movebisLoading",
  "stepper",
]

// Try to fix : Error on imported module
// Source : https://stackoverflow.com/questions/69300341/typeerror-failed-to-fetch-dynamically-imported-module-on-vue-vite-vanilla-set
router.onError((error, to) => {
  if (
    error.message.includes("Failed to fetch dynamically imported module") ||
    error.message.includes("Importing a module script failed")
  ) {
    window.location.href = to.fullPath
  }
})

router.afterEach((to, from) => {
  // const mixpanel = inject("mixpanel") as CustomMixpanel
  document.title =
    to.name === "start"
      ? "Assessment onboarding | AssessFirst"
      : t(to.meta.title as string)

  // dans l'idéal on devrait envoyer un event à la machine global
  // mais je ne parviens à réinitialiser la shapeMachine, du coup ça pète le flow XState
  // donc plan B: pour l'instant on reload la page
  // globalActor.send({ type: "GLOBAL.GOTO.STEPPER" })

  if (
    to.name === "stepper" &&
    from.name !== "loading" &&
    from.matched[0]?.name !== "screening"
  ) {
    window.location.reload()
    history.pushState(null, "", from.fullPath) // remove last entry from history to disable previous button in the browser
  }

  if (
    from.name &&
    !pagesToIgnore.includes(from.name) &&
    isMixpanelInitialized()
  ) {
    MixpanelInstance.track(`Leave ${from.name.toString()}`)
  }

  // start new time reporter, and enter log
  if (to.name && !pagesToIgnore.includes(to.name) && isMixpanelInitialized()) {
    MixpanelInstance.time_event(`Leave ${to.name.toString()}`)
    MixpanelInstance.track(`Enter ${to.name.toString()}`, {
      entry_point: penultimatePage !== "" ? penultimatePage : from.name,
    })
  }

  if (pagesToIgnore.includes(to.name)) {
    penultimatePage = from.name || ""
  } else {
    penultimatePage = "" // reset penultimatePage
  }
})

export default router
